package de.xtl.retroguide.question

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Button
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import de.xtl.retroguide.AppTheme
import de.xtl.retroguide.CelebrationConfetti
import de.xtl.retroguide.join.JoinSessionAction
import de.xtl.retroguide.join.JoinSessionState
import de.xtl.retroguide.protocol.LobbyParticipant
import de.xtl.retroguide.protocol.ParticipantRole
import de.xtl.retroguide.protocol.QuestionSummary
import de.xtl.retroguide.protocol.RetroPhase
import de.xtl.retroguide.protocol.RetroQuestion
import de.xtl.retroguide.protocol.SessionAccess
import de.xtl.retroguide.protocol.SessionSnapshot
import de.xtl.retroguide.protocol.VisibleAnswer
import org.jetbrains.compose.resources.stringResource
import retroguide.app.shared.generated.resources.Res
import retroguide.app.shared.generated.resources.download_markdown_action
import retroguide.app.shared.generated.resources.moderator_summary_title
import retroguide.app.shared.generated.resources.no_answers
import retroguide.app.shared.generated.resources.question_progress
import retroguide.app.shared.generated.resources.registered_participants
import retroguide.app.shared.generated.resources.retro_finished
import retroguide.app.shared.generated.resources.session_ended_action
import retroguide.app.shared.generated.resources.share_retro_action

@Composable
fun ModeratorSummaryScreen(
    state: JoinSessionState,
    onAction: (JoinSessionAction) -> Unit,
) {
    val session = state.session ?: return
    val participantCount = session.participants.count { it.role == ParticipantRole.PARTICIPANT }
    Surface(Modifier.fillMaxSize()) {
        Box(
            modifier = Modifier.fillMaxSize().safeDrawingPadding(),
            contentAlignment = Alignment.TopCenter,
        ) {
            Box(
                modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()),
                contentAlignment = Alignment.TopCenter,
            ) {
                Column(
                    modifier = Modifier
                        .widthIn(max = 412.dp)
                        .fillMaxWidth()
                        .padding(horizontal = 24.dp, vertical = 48.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp),
                ) {
                    Text(stringResource(Res.string.moderator_summary_title), style = MaterialTheme.typography.titleLarge)
                    Text(
                        stringResource(Res.string.retro_finished),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                    Text(
                        stringResource(Res.string.registered_participants, participantCount),
                        style = MaterialTheme.typography.titleMedium,
                    )
                    session.questionSummaries.forEachIndexed { index, summary ->
                        Text(
                            stringResource(Res.string.question_progress, index + 1, session.questionSummaries.size),
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                        Text(summary.question.text, style = MaterialTheme.typography.headlineSmall)
                        if (summary.answers.isEmpty() && summary.answerChoices.isEmpty()) {
                            Text(stringResource(Res.string.no_answers), style = MaterialTheme.typography.bodyLarge)
                        } else {
                            AnswerBoard(summary.answers, summary.question.widget, summary.clusters, summary.votingOptions) {
                                ParticipantAnswer(it, summary.question.widget)
                            }
                            summary.answerChoices.forEach { ParticipantAnswerChoiceCard(it) }
                        }
                    }
                    Button(
                        onClick = { onAction(JoinSessionAction.OnDownloadRetroClick) },
                        modifier = Modifier.fillMaxWidth(),
                    ) {
                        Text(stringResource(Res.string.download_markdown_action))
                    }
                    OutlinedButton(
                        onClick = { onAction(JoinSessionAction.OnShareRetroClick) },
                        modifier = Modifier.fillMaxWidth(),
                    ) {
                        Text(stringResource(Res.string.share_retro_action))
                    }
                    TextButton(
                        onClick = { onAction(JoinSessionAction.OnSummaryDoneClick) },
                        modifier = Modifier.align(Alignment.CenterHorizontally),
                    ) {
                        Text(stringResource(Res.string.session_ended_action))
                    }
                }
            }
            CelebrationConfetti(Modifier.matchParentSize())
        }
    }
}

@Preview
@Composable
private fun ModeratorSummaryScreenPreview() {
    val moderator = LobbyParticipant("1", "Mara", ParticipantRole.MODERATOR)
    val participants = listOf("Alex", "Sam", "Lea", "Tobi", "Nina", "Omar").mapIndexed { index, name ->
        LobbyParticipant("${index + 2}", name, ParticipantRole.PARTICIPANT)
    }
    val firstQuestion = RetroQuestion("question-1", "Was lief in diesem Sprint gut?", 300)
    val secondQuestion = RetroQuestion("question-2", "Was möchten wir im nächsten Sprint verbessern?", 300)
    AppTheme {
        ModeratorSummaryScreen(
            state = JoinSessionState(
                sessionCode = "7KD4MQ",
                access = SessionAccess("7KD4MQ", moderator.id, "preview-token", moderator.role, "Team-Retro"),
                session = SessionSnapshot(
                    sessionCode = "7KD4MQ",
                    title = "Team-Retro",
                    participants = listOf(moderator) + participants,
                    phase = RetroPhase.FINISHED,
                    questionCount = 2,
                    questionSummaries = listOf(
                        QuestionSummary(
                            firstQuestion,
                            listOf(
                                VisibleAnswer("answer-1", "Alex", "Die Zusammenarbeit im Pairing."),
                                VisibleAnswer("answer-2", "Sam", "Die kurzen Abstimmungswege."),
                            ),
                        ),
                        QuestionSummary(
                            secondQuestion,
                            listOf(VisibleAnswer("answer-3", "Lea", "Reviews früher einplanen.")),
                        ),
                    ),
                ),
            ),
            onAction = {},
        )
    }
}
